'use client';

import React from 'react';

import { Carousel } from '@depop/web-ui-kit/Carousel';

import { Banner } from '@/components/Banner';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type TransformedWebBannerResponse } from '@/modules/cms/types';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { TrackingType } from '@/modules/home/types.ts';
import { getBannerImagesForVariant } from '@/modules/home/utils';

type Props = {
  webBannerData: TransformedWebBannerResponse[];
};

export function MarketingCarouselUi({ webBannerData }: Props) {
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { web_7707_broaden_appeal_imagery } = useExperiments([
    'web_7707_broaden_appeal_imagery',
  ]);

  function onClick(item: TransformedWebBannerResponse, itemPosition?: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: TrackingType.skinnyBanner,
      itemClicked: item.heading.replace(/ /g, '_'),
      itemPosition,
      schemaVersion: '3.0',
    });
  }

  const items = webBannerData.map((item, index) => {
    const { mobileImageUrl, desktopImageUrl } = getBannerImagesForVariant(
      item,
      web_7707_broaden_appeal_imagery
    );

    return (
      <Banner
        key={index}
        title={item.heading}
        body={item.bodyJson}
        cta={{
          copy: item.ctaCopy,
          url: item.ctaHref,
          onClick: () => onClick(item, index),
        }}
        desktopBackgroundImage={desktopImageUrl}
        mobileBackgroundImage={mobileImageUrl}
      />
    );
  });

  return (
    <Carousel
      items={items}
      itemsPerSlide={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
      showNextPreview={false}
      showPreviousPreview={false}
      infinityMode={true}
      autoScroll={true}
      hideButtons={{
        desktop: { prev: true },
        mobile: { prev: true, next: true },
      }}
      indicators={true}
      navButtonOffsetX={16}
      itemGap={0}
      inert={false}
      aria-label="Marketing campaigns"
    />
  );
}
